import { useMemo, useState } from "react";
import { useGetResignationListQuery } from "../../api/resignationApi";
import {
  QUEUE_FILTERS,
  apiErrorMessage,
  effectiveStatus,
  formatDate,
  formatNoticePeriod,
  normalizeResignationList,
} from "./constants";
import StatusBadge from "./StatusBadge";
import InitiateResignationDialog from "./InitiateResignationDialog";

const ResignationQueue = ({ onSelect }) => {
  const { data, isLoading, isFetching, isError, error, refetch } = useGetResignationListQuery();
  const [filter, setFilter] = useState("ALL");
  const [initiateOpen, setInitiateOpen] = useState(false);

  const records = useMemo(() => normalizeResignationList(data), [data]);

  const counts = useMemo(
    () =>
      records.reduce((acc, record) => {
        const status = effectiveStatus(record);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [records]
  );

  const rows = records.filter(
    (record) => filter === "ALL" || effectiveStatus(record) === filter
  );

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Resignation Queue</h2>
          <p className="text-sm text-gray-500">
            Review resignation requests and exit-clearance documents.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {records.length} record{records.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={() => setInitiateOpen(true)}
            className="rounded-lg bg-blue-primary px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
          >
            Initiate Resignation
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {QUEUE_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filter === option.value
                ? "border-blue-primary bg-blue-primary text-white"
                : "border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
          >
            {option.label}
            {option.value !== "ALL" && counts[option.value] ? ` (${counts[option.value]})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading queue...</p>
        ) : isError ? (
          <div className="py-6 text-center">
            <p className="text-sm text-red-600">
              {apiErrorMessage(error, "Could not load the resignation queue.")}
            </p>
            <button
              type="button"
              onClick={refetch}
              className="mt-3 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">
            No requests match this filter.
          </p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                <th className="py-2 pr-4">Employee</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Submitted</th>
                <th className="py-2 pr-4">Notice Period</th>
                <th className="py-2 pr-4">Last Working Day</th>
                <th className="py-2 pr-4">Documents</th>
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((record) => (
                <tr
                  key={record.employeeId || record.id}
                  onClick={() => onSelect(record.employeeId)}
                  className="cursor-pointer border-b border-gray-100 hover:bg-blue-secondery-2"
                >
                  <td className="py-3 pr-4 text-sm font-medium text-gray-700">
                    {record.employeeName || "-"}
                    <span className="block text-xs text-gray-400">{record.employeeNum}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <StatusBadge status={effectiveStatus(record)} />
                  </td>
                  <td className="py-3 pr-4 text-sm text-gray-500">
                    {formatDate(record.submittedDate)}
                  </td>
                  <td className="py-3 pr-4 text-sm text-gray-500">
                    {formatNoticePeriod(record)}
                  </td>
                  <td className="py-3 pr-4 text-sm text-gray-500">
                    {formatDate(record.lastWorkingDay)}
                  </td>
                  <td className="py-3 pr-4 text-sm text-gray-500">
                    {record.documents?.length || 0}
                  </td>
                  <td className="py-3 pr-4 text-sm font-medium text-blue-primary">
                    View &rarr;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isFetching && !isLoading && (
        <p className="mt-2 text-xs text-gray-400">Refreshing...</p>
      )}

      <InitiateResignationDialog
        open={initiateOpen}
        records={records}
        onClose={() => setInitiateOpen(false)}
        onSubmitted={(employeeId) => {
          setInitiateOpen(false);
          onSelect(employeeId);
        }}
      />
    </section>
  );
};

export default ResignationQueue;
